import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusPill } from '@/components/StatusPill';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';

export function IndustriesSection({
  industries = [],
  deptCountByIndustry = {},
  roleCountByIndustry = {},
  isLoading,
  isError,
  onSelectIndustry,
  onNewIndustry,
  onRequestDelete,
}) {
  const stopRowNav = (e) => {
    e.stopPropagation();
  };

  const askDeleteIndustry = (ind, deptCount, roleCount) => {
    if (deptCount > 0 || roleCount > 0) return;
    onRequestDelete?.({
      kind: 'industry',
      ids: [ind.id],
      label: `Delete industry "${ind.name}"? It will be removed from master lists but kept in the database.`,
    });
  };

  return (
    <Card className="card-elevated flex h-full min-h-0 flex-col overflow-hidden" data-testid="industries-section">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b p-4">
        <div>
          <p className="text-sm font-semibold">Industries</p>
          <p className="text-xs text-muted-foreground">
            {isLoading ? 'Loading…' : `${industries.length} industr${industries.length === 1 ? 'y' : 'ies'} · select one to manage departments`}
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={onNewIndustry} data-testid="industry-new-button">
          <Plus className="h-4 w-4" /> New industry
        </Button>
      </div>
      {isError ? (
        <p className="p-4 text-sm text-destructive" data-testid="industries-error">Could not load industries.</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 z-10 bg-secondary/40">
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Departments</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {industries.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                    No industries yet. Create an industry to start the Industry → Department → Role hierarchy.
                  </TableCell>
                </TableRow>
              ) : (
                industries.map((ind) => {
                  const deptCount = deptCountByIndustry[ind.id] ?? 0;
                  const roleCount = roleCountByIndustry[ind.id] ?? 0;
                  const canDelete = deptCount === 0 && roleCount === 0;
                  const blockedReason = deptCount > 0
                    ? `Remove all departments before deleting ${ind.name}`
                    : roleCount > 0
                      ? `Remove all roles before deleting ${ind.name}`
                      : undefined;
                  return (
                    <TableRow
                      key={ind.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => onSelectIndustry?.(ind)}
                      data-testid="industry-row"
                    >
                      <TableCell>
                        <code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{ind.code}</code>
                      </TableCell>
                      <TableCell className="font-medium">{ind.name}</TableCell>
                      <TableCell><StatusPill status={ind.status} size="sm" /></TableCell>
                      <TableCell>
                        <span
                          className="inline-flex rounded-md bg-secondary px-2 py-0.5 text-xs font-medium tabular-nums"
                          data-testid={`industry-dept-count-${ind.code}`}
                        >
                          {deptCount}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-flex rounded-md bg-secondary px-2 py-0.5 text-xs font-medium tabular-nums"
                          data-testid={`industry-role-count-${ind.code}`}
                        >
                          {roleCount}
                        </span>
                      </TableCell>
                      <TableCell className="text-right" onClick={stopRowNav}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 px-2 text-rose-700 hover:bg-rose-50 disabled:text-muted-foreground"
                            disabled={!canDelete}
                            title={blockedReason || `Delete industry ${ind.name}`}
                            aria-label={blockedReason || `Delete industry ${ind.name}`}
                            onClick={() => askDeleteIndustry(ind, deptCount, roleCount)}
                            data-testid={`delete-industry-button-${ind.code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </Button>
                          <ChevronRight className="ml-0.5 h-4 w-4 text-muted-foreground" aria-hidden />
                          <span className="sr-only">Open departments for {ind.name}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
